import React from 'react';
import { Badge, useTabState, type BadgeProps } from '@tempmee/react';

/**
 * Renders a {@link Badge} counter/dot inside a `<Tab>`, coloring it from
 * the tab's active state via {@link useTabState} — the single source of truth for
 * active/inactive. Callers pass only their own data; never recompute the active
 * tab at the call site.
 *
 * This is the intended integration pattern, not a stopgap: v3 deliberately keeps
 * the DS `Tab` unopinionated (no built-in counter — it was removed on purpose).
 * The DS exposes `Badge` + `useTabState()` (state) and leaves the
 * active/inactive *convention* to the app. There is no future "DS tab badge" to
 * adopt — composing the two here is how it's meant to be done.
 *
 * App-local by design: delete this only when the counter-in-tab pattern itself
 * is phased out (we stop showing counts on tabs), NOT because the DS will absorb it.
 */
type TTabBadgeProps = {
  count?: number;
  dot?: boolean;
  /** Counts above this render as `${max}+`. Forwarded to {@link Badge} (defaults to 99). */
  max?: number;
  /** Color when the tab is active. Inactive falls back to `inactiveColor`. */
  activeColor?: BadgeProps['severity'];
  inactiveColor?: BadgeProps['severity'];
};

export function TabBadge({
  count,
  dot,
  max,
  activeColor = 'info',
  inactiveColor = 'secondary',
}: TTabBadgeProps) {
  const { selected } = useTabState(); // <-- the only place active/inactive is decided
  const severity = selected ? activeColor : inactiveColor;

  if (dot) return <Badge severity={severity} variant="dot" />;
  if (count == null) return null;
  return <Badge severity={severity} count={count} max={max} />;
}
